import { Show, DeleteButton, DateField } from "@refinedev/antd";
import { useShow, useList } from "@refinedev/core";
import {
  Descriptions,
  Space,
  Tag,
  Row,
  Col,
  Card,
  Table,
  Typography,
  Empty,
} from "antd";
import {
  TeamOutlined,
  FileTextOutlined,
  FlagOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import type { Brain, Entry } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { CategoryTag } from "@/components/CategoryTag";
import { formatDate, truncate } from "@/lib/format";

const { Text, Paragraph } = Typography;

/**
 * /admin/brains/:id — detalhe de moderação de Brain.
 *
 * Como obtém os dados (MOCK MODE): `useShow` traz o Brain (getOne) e um
 * `useList` filtrado por `brain_id` puxa uma amostra das entradas para a
 * moderação. Privacidade preservada: entradas `is_private` aparecem apenas como
 * rótulo, sem revelar o texto (LGPD §11).
 */
export const BrainShow: React.FC = () => {
  const { queryResult } = useShow<Brain>();
  const { data, isLoading } = queryResult;
  const b = data?.data;

  // Amostra de entradas do Brain (para moderação) — só carrega quando há id.
  const { data: entriesData, isLoading: loadingEntries } = useList<Entry>({
    resource: "entries",
    filters: b?.id ? [{ field: "brain_id", operator: "eq", value: b.id }] : [],
    sorters: [{ field: "created_at", order: "desc" }],
    pagination: { pageSize: 10 },
    queryOptions: { enabled: !!b?.id },
  });
  const entries = useMemo(() => entriesData?.data ?? [], [entriesData]);
  const flaggedEntries = useMemo(() => entries.filter((e) => e.flagged).length, [entries]);

  return (
    <Show
      isLoading={isLoading}
      title="Brain · moderação"
      headerButtons={
        <DeleteButton recordItemId={b?.id} danger>
          Remover (moderação)
        </DeleteButton>
      }
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {/* KPIs do Brain. */}
        <Row gutter={[16, 16]}>
          <Col xs={12} lg={6}>
            <StatCard
              title="Membros"
              value={b?.members_count ?? 0}
              prefix={<TeamOutlined />}
              hint={(b?.members_count ?? 0) > 1 ? "compartilhado" : "individual"}
            />
          </Col>
          <Col xs={12} lg={6}>
            <StatCard
              title="Entradas"
              value={b?.entries_count ?? 0}
              prefix={<FileTextOutlined />}
              hint="total armazenadas"
            />
          </Col>
          <Col xs={12} lg={6}>
            <StatCard
              title="Denunciadas"
              value={flaggedEntries}
              prefix={<FlagOutlined />}
              hint="na amostra recente"
            />
          </Col>
          <Col xs={12} lg={6}>
            <StatCard
              title="Default"
              value={b?.is_default ? "Sim" : "Não"}
              hint="Brain padrão do dono"
            />
          </Col>
        </Row>

        <Descriptions
          bordered
          column={{ xs: 1, sm: 2 }}
          size="small"
          title={
            <Space>
              <span style={{ fontSize: 20 }}>{b?.emoji ?? "🧠"}</span>
              <Text strong>{b?.nome}</Text>
              {b?.is_default && <Tag color="cyan">default</Tag>}
              {b?.flagged && (
                <Tag color="red" icon={<FlagOutlined />}>
                  denunciado
                </Tag>
              )}
            </Space>
          }
        >
          <Descriptions.Item label="Contexto" span={2}>
            {b?.contexto ? (
              <Paragraph style={{ marginBottom: 0 }}>{b.contexto}</Paragraph>
            ) : (
              <Text type="secondary">— sem contexto —</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Dono (user_id)">
            <Text code>{b?.user_id ?? "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="ID">
            <Text code>{b?.id ?? "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Criado em" span={2}>
            {formatDate(b?.created_at)}
          </Descriptions.Item>
        </Descriptions>

        <Card
          className="mel-glass"
          bordered={false}
          title="Entradas recentes (amostra)"
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              <LockOutlined /> conteúdo privado preservado
            </Text>
          }
        >
          <Table<Entry>
            dataSource={entries}
            loading={loadingEntries}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 700 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nenhuma entrada neste Brain"
                />
              ),
            }}
          >
            <Table.Column
              dataIndex="categoria"
              title="Categoria"
              render={(c: Entry["categoria"]) => <CategoryTag value={c} />}
            />
            <Table.Column<Entry>
              title="Texto"
              render={(_, row) =>
                row.is_private ? (
                  <Tag color="magenta" icon={<LockOutlined />}>
                    privado (oculto)
                  </Tag>
                ) : (
                  <Text>{truncate(row.texto_corrigido ?? row.texto_original, 70)}</Text>
                )
              }
            />
            <Table.Column
              dataIndex="flagged"
              title="Flag"
              width={120}
              render={(f?: boolean) =>
                f ? (
                  <Tag color="red" icon={<FlagOutlined />}>
                    denunciado
                  </Tag>
                ) : (
                  <Tag>ok</Tag>
                )
              }
            />
            <Table.Column
              dataIndex="created_at"
              title="Criado"
              width={150}
              render={(v: string) => <DateField value={v} format="DD/MM/YYYY HH:mm" />}
            />
          </Table>
        </Card>
      </Space>
    </Show>
  );
};
